import { useMemo } from 'react';
import { AlertTriangle, ShieldAlert, Plane } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';

// Global Aerospace option ladder — the broker picks the hull basis, liability
// tier, and medical option before anything lands.
//
// WHY THIS EXISTS: Global's totalPremium is its DEFAULT hull basis (F. All
// Risks, the most expensive of three). Landing it over-quotes — measured $3,100
// on a PA-24-250 where the same quote at the G basis is $2,020. MyGlobal makes
// the broker choose; so does this. Nothing here auto-commits: the confirm button
// stays disabled until every aircraft has a basis and a liability tier is picked.
//
// All money comes from the server-computed ladder. This component sums the
// SELECTED premiums for live display only; the number that actually lands is
// re-priced server-side from the persisted quote (/api/global/quote/land), so
// what the broker sees and what is written can never diverge from Global's own
// per-option premiums.

const money = (n) =>
  n == null ? '\u2014' : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

// One rung. Two states:
//   base       the rung the client applied for, a radio, always the logged quote
//   alternate  any OTHER rung the broker may tag as an offer, a checkbox —
//              including rungs BELOW the applied-for coverage, which wear the
//              "below requested" annotation instead of a strikethrough.
//              Suppression died with prompt 181 §2 (David: "if i WANT to offer
//              different / lower options, i should have that prerogative"):
//              below-request is a fact about the option, not a refusal, and
//              Global priced every rung it returned. Zero extra clicks.
function OptionRow({ name, checked, onChange, title, sub, premium, delta, mode = 'base' }) {
  const isAlt = mode === 'alternate';
  return (
    <label
      className={`flex cursor-pointer items-center gap-2.5 rounded-md border px-2.5 py-2 transition ${
        checked ? 'border-info bg-info-soft' : 'border-border hover:border-border-strong'
      }`}
    >
      <input
        type={isAlt ? 'checkbox' : 'radio'}
        name={isAlt ? undefined : name}
        checked={checked}
        onChange={onChange}
        className="h-3.5 w-3.5 shrink-0 accent-[#60a5fa]"
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] text-text">{title}</span>
        {sub ? <span className="block truncate text-[11px] text-text-dim">{sub}</span> : null}
      </span>
      <span className="shrink-0 text-right">
        <span className="block text-[13px] font-medium tabular-nums text-text">{money(premium)}</span>
        {delta ? <span className="block text-[10px] tabular-nums text-text-dim">{delta}</span> : null}
      </span>
    </label>
  );
}

export default function GlobalLadderDialog({
  open,
  onOpenChange,
  ladder,
  selection,
  onSelectionChange,
  busy,
  onConfirm,
  // "Also present" alternates: richer rungs the broker tags to ride along with
  // the as-applied quote. Keyed `${dimension}:${id}` so a pick is idempotent.
  alternates = {},
  onAlternatesChange,
}) {
  // Live display total. The authoritative re-price happens server-side on
  // confirm; this only tells the broker what they are looking at. Derived from
  // `ladder`/`selection` alone so the memo has stable dependencies.
  const totals = useMemo(() => {
    const us = ladder?.units || [];
    const liabs = ladder?.liability || [];
    const meds = ladder?.medical || [];
    let hull = 0;
    let hullPicked = 0;
    for (const u of us) {
      const opt = (u.options || []).find((o) => o.coverageType === selection?.hullByUnitId?.[u.unitId]);
      if (opt) { hull += opt.premium || 0; hullPicked += 1; }
    }
    const liab = liabs.find((o) => o.id === selection?.liabilityId) || null;
    const med = meds.find((o) => o.index === selection?.medicalIndex) || null;
    const premium = hull + (liab?.premium || 0) + (med?.premium || 0);
    const pct = ladder?.surchargePercent;
    const surcharge = pct ? Math.round(premium * (pct / 100) * 100) / 100 : (ladder?.defaultSurcharge || 0);
    return {
      hull, premium, surcharge,
      amount: premium + (surcharge || 0),
      complete: hullPicked === us.length && (!liabs.length || !!liab),
    };
  }, [ladder, selection]);

  const units = ladder?.units || [];
  const liability = ladder?.liability || [];
  const medical = ladder?.medical || [];

  const toggleAlt = (key, payload) => {
    if (!onAlternatesChange) return;
    const next = { ...alternates };
    if (next[key]) delete next[key]; else next[key] = payload;
    onAlternatesChange(next);
  };

  const setHull = (unitId, coverageType) =>
    onSelectionChange({ ...selection, hullByUnitId: { ...(selection?.hullByUnitId || {}), [unitId]: coverageType } });

  const defaultTotal = ladder?.defaultTotal;
  const savings = defaultTotal != null && totals.complete && totals.premium < defaultTotal
    ? defaultTotal - totals.premium
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Global Aerospace: what the client applied for</DialogTitle>
          <DialogDescription>
            The base is what the client applied for and it is what gets logged. Tick any richer
            rung to log it as its own option on the same quote, with its limits and its full premium.
            You choose which options the client sees when you present. Nothing below what they
            applied for is offered.
          </DialogDescription>
        </DialogHeader>

        {ladder?.multiUnitUnverified ? (
          <p className="flex gap-2 rounded-md border border-warning/40 bg-warning-soft px-2.5 py-2 text-[11px] leading-snug text-text">
            <AlertTriangle size={13} strokeWidth={1.8} className="mt-0.5 shrink-0 text-warning" aria-hidden="true" />
            <span>
              Multi-aircraft ladder. The per-aircraft hull options are matched on hull value and this
              mapping has not yet been checked against a live multi-aircraft Global quote. Confirm each
              aircraft against MyGlobal before presenting.
            </span>
          </p>
        ) : null}

        {units.map((u) => (
          <div key={u.unitId} className="space-y-1.5">
            <div className="flex items-baseline justify-between gap-2">
              <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-text-muted">
                <Plane size={12} strokeWidth={1.8} aria-hidden="true" />
                {u.registration || `Aircraft ${u.unitId}`}
                {u.makeModel ? <span className="normal-case tracking-normal text-text-dim">{u.makeModel}</span> : null}
              </span>
              <span className="text-[11px] tabular-nums text-text-dim">{money(u.insuredValue)} hull</span>
            </div>
            {(u.options || []).map((o) => {
              const sub = [
                o.short,
                o.inMotionDeductible || o.notInMotionDeductible
                  ? `${money(o.inMotionDeductible)} in motion / ${money(o.notInMotionDeductible)} not in motion`
                  : null,
              ].filter(Boolean).join(' · ');
              // The applied rung is the base and stays a radio.
              if (o.isApplied) {
                return (
                  <OptionRow
                    key={o.coverageType}
                    name={`hull-${u.unitId}`}
                    checked={selection?.hullByUnitId?.[u.unitId] === o.coverageType}
                    onChange={() => setHull(u.unitId, o.coverageType)}
                    title={o.label} sub={sub} premium={o.premium} delta="as applied for"
                  />
                );
              }
              const key = `hull:${u.unitId}:${o.coverageType}`;
              return (
                <OptionRow
                  key={o.coverageType}
                  mode="alternate"
                  checked={!!alternates[key]}
                  onChange={() => toggleAlt(key, {
                    dimension: 'hull', unit_id: u.unitId, registration: u.registration,
                    coverage_type: o.coverageType, label: o.label, premium: o.premium,
                  })}
                  title={o.label} sub={sub} premium={o.premium} delta={o.below_requested ? 'below requested' : 'also present'}
                />
              );
            })}
          </div>
        ))}

        {liability.length ? (
          <div className="space-y-1.5 border-t border-border pt-3">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Liability</span>
            {liability.map((o) => {
              if (o.isApplied) {
                return (
                  <OptionRow key={o.id} name="liability"
                    checked={selection?.liabilityId === o.id}
                    onChange={() => onSelectionChange({ ...selection, liabilityId: o.id })}
                    title={o.label} premium={o.premium} delta="as applied for" />
                );
              }
              const key = `liability:${o.id}`;
              return (
                <OptionRow key={o.id} mode="alternate"
                  checked={!!alternates[key]}
                  onChange={() => toggleAlt(key, {
                    dimension: 'liability', option_id: o.id, label: o.label, premium: o.premium,
                  })}
                  title={o.label} premium={o.premium} delta={o.below_requested ? 'below requested' : 'also present'} />
              );
            })}
          </div>
        ) : null}

        {medical.length ? (
          <div className="space-y-1.5 border-t border-border pt-3">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">Medical payments</span>
            {medical.map((o) => {
              if (o.isApplied) {
                return (
                  <OptionRow key={o.index} name="medical"
                    checked={selection?.medicalIndex === o.index}
                    onChange={() => onSelectionChange({ ...selection, medicalIndex: o.index })}
                    title={o.label} premium={o.premium} delta="as applied for" />
                );
              }
              const key = `medical:${o.index}`;
              return (
                <OptionRow key={o.index} mode="alternate"
                  checked={!!alternates[key]}
                  onChange={() => toggleAlt(key, {
                    dimension: 'medical', option_index: o.index, label: o.label, premium: o.premium,
                  })}
                  title={o.label} premium={o.premium} delta={o.below_requested ? 'below requested' : 'also present'} />
              );
            })}
          </div>
        ) : null}

        <div className="border-t border-border pt-3">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-text-muted">Selected premium</span>
            <span className="text-lg font-semibold tabular-nums text-text">{money(totals.premium)}</span>
          </div>
          {totals.surcharge ? (
            <div className="flex items-baseline justify-between text-[11px] text-text-dim">
              <span>plus {money(totals.surcharge)} surcharge</span>
              <span className="tabular-nums">{money(totals.amount)} to the client</span>
            </div>
          ) : null}
          {savings ? (
            <p className="mt-1 text-[11px] text-success">
              {money(savings)} below Global&rsquo;s default basis ({money(defaultTotal)}).
            </p>
          ) : null}
          {!totals.complete ? (
            <p className="mt-1 flex items-center gap-1.5 text-[11px] text-accent">
              <ShieldAlert size={12} strokeWidth={1.8} aria-hidden="true" />
              Pick a basis for every aircraft and a liability tier to continue.
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={busy || !totals.complete}>
            {busy
              ? 'Logging\u2026'
              : `Log ${money(totals.premium)}${Object.keys(alternates).length ? ` + ${Object.keys(alternates).length} to present` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
